import { useState, useMemo, useCallback, useEffect } from 'react'
import {
  Search, Package, Truck, CheckCircle, XCircle, Clock, RefreshCw,
  Eye, ChevronDown, Calendar, Download, Filter, Printer,
  ArrowRight, User, MapPin, StickyNote, Hash, AlertCircle,
  CheckSquare, List,
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { StatusBadge, Modal, Spinner, EmptyState, Pagination } from '../components/ui'
import { orderService } from '../services/api'
import toast from 'react-hot-toast'

// Authenticated download of an order's POS import file (stock_code + quantity).
// Works even if the order email failed — the file is rebuilt from the saved order.
async function downloadOrderPosCsv(orderId) {
  try {
    const blob = await orderService.downloadPosCsv(orderId)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `order-${orderId}.csv`
    document.body.appendChild(a); a.click(); a.remove()
    URL.revokeObjectURL(url)
  } catch (e) {
    toast.error('Could not download the order file')
  }
}

// ─── Constants ──────────────────────────────────────────────────────────────

const STATUS_OPTIONS = ['all', 'pending', 'processing', 'shipped', 'delivered', 'cancelled']

const STATUS_META = {
  pending:    { label: 'Pending',    icon: Clock,        color: 'text-amber-400',  bg: 'bg-amber-400/10',  border: 'border-amber-400/20' },
  processing: { label: 'Processing', icon: Package,      color: 'text-blue-400',   bg: 'bg-blue-400/10',   border: 'border-blue-400/20' },
  shipped:    { label: 'Shipped',    icon: Truck,        color: 'text-indigo-400', bg: 'bg-indigo-400/10', border: 'border-indigo-400/20' },
  delivered:  { label: 'Delivered',  icon: CheckCircle,  color: 'text-green-400',  bg: 'bg-green-400/10',  border: 'border-green-400/20' },
  cancelled:  { label: 'Cancelled',  icon: XCircle,      color: 'text-red-400',    bg: 'bg-red-400/10',    border: 'border-red-400/20' },
}

const STATUS_TRANSITIONS = {
  pending:    [{ next: 'processing', label: 'Confirm', icon: Package,     cls: 'btn-primary' },
               { next: 'cancelled',  label: 'Cancel',  icon: XCircle,     cls: 'btn-danger' }],
  processing: [{ next: 'shipped',    label: 'Ship',    icon: Truck,       cls: 'btn-primary' },
               { next: 'cancelled',  label: 'Cancel',  icon: XCircle,     cls: 'btn-danger' }],
  shipped:    [{ next: 'delivered',  label: 'Deliver', icon: CheckCircle, cls: 'btn-success' }],
}

const BULK_STATUS_OPTIONS = ['processing', 'shipped', 'delivered', 'cancelled']

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-ZA') : '—'
const fmtR = (n) => `R ${parseFloat(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// ─── Status Timeline ────────────────────────────────────────────────────────

function StatusTimeline({ history }) {
  const FLOW = ['pending', 'processing', 'shipped', 'delivered']
  const reached = new Set((history || []).map(h => h.status))
  const isCancelled = reached.has('cancelled')

  if (isCancelled) {
    return (
      <div className="flex items-center gap-2 text-red-400 text-sm py-2">
        <XCircle size={16}/> Order was cancelled
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {FLOW.map((s, i) => {
        const meta = STATUS_META[s]
        const done = reached.has(s)
        const Icon = meta.icon
        return (
          <div key={s} className="flex items-center gap-1">
            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              done ? `${meta.bg} ${meta.color} border ${meta.border}` : 'bg-surface-elevated text-gray-600 border border-surface-border'
            }`}>
              <Icon size={12}/>
              {meta.label}
            </div>
            {i < FLOW.length - 1 && (
              <ArrowRight size={12} className={done && reached.has(FLOW[i+1]) ? 'text-gray-500' : 'text-gray-700'}/>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Order Detail Modal ──────────────────────────────────────────────────────

function OrderDetailModal({ order, onClose, onStatusUpdate, isUpdating }) {
  const { data: detail, isLoading } = useQuery({
    queryKey: ['admin', 'order-detail', order.id],
    queryFn: () => orderService.getDetail(order.id),
    staleTime: 30 * 1000,
  })

  const d = detail || order
  const items = d.items || []
  const history = d.status_history || []
  const transitions = STATUS_TRANSITIONS[d.status] || []

  const handlePrint = () => {
    const win = window.open('', '_blank')
    const rows = items.map(item => `
      <tr>
        <td>${item.stock_code || '—'}</td>
        <td>${item.description || '—'}</td>
        <td>${item.brand || '—'}</td>
        <td style="text-align:center">${item.quantity}</td>
        <td style="text-align:right">R ${parseFloat(item.unit_price||0).toFixed(2)}</td>
        <td style="text-align:right">R ${parseFloat(item.total_price||0).toFixed(2)}</td>
      </tr>`).join('')
    win.document.write(`
      <html><head><title>Order #${d.id}</title>
      <style>body{font-family:Arial;padding:20px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:8px;font-size:13px}th{background:#0e947a;color:#fff}h2{color:#0e947a}</style>
      </head><body>
      <h2>Tony's Liquor — Order #${d.id}</h2>
      <p><strong>Customer:</strong> ${d.customer_name || 'N/A'} &nbsp; <strong>Date:</strong> ${fmtTime(d.order_date || d.created_at)} &nbsp; <strong>Status:</strong> ${d.status}</p>
      <p><strong>Delivery:</strong> ${d.delivery_address || 'N/A'}</p>
      <table><thead><tr><th>Stock Code</th><th>Product</th><th>Brand</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <p style="text-align:right;font-size:16px;margin-top:12px"><strong>ORDER TOTAL: R ${parseFloat(d.total_amount||0).toFixed(2)}</strong></p>
      </body></html>`)
    win.print()
  }

  const handleCSV = () => {
    const header = 'Stock Code,Description,Brand,Qty,Unit Price,Total\n'
    const rows = items.map(i =>
      `${i.stock_code||''},${(i.description||'').replace(/,/g,'')},${i.brand||''},${i.quantity},${i.unit_price||0},${i.total_price||0}`
    ).join('\n')
    const blob = new Blob([header + rows], { type: 'text/csv' })
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob)
    a.download = `order-${d.id}.csv`; a.click()
  }

  return (
    <Modal title={`Order #${d.id}`} onClose={onClose} size="xl">
      {isLoading ? (
        <div className="flex items-center justify-center py-12"><Spinner size="lg"/></div>
      ) : (
        <div className="space-y-5 max-h-[80vh] overflow-y-auto pr-1">
          {/* Status timeline */}
          <div className="bg-surface-elevated rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">Order Progress</p>
            <StatusTimeline history={history}/>
          </div>

          {/* Info grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-surface-elevated rounded-xl p-3 col-span-2 sm:col-span-1">
              <div className="flex items-center gap-2 text-gray-400 text-xs mb-2"><User size={13}/> Customer</div>
              <p className="text-white font-semibold">{d.customer_name || 'Unknown'}</p>
              {d.customer_email && <p className="text-gray-400 text-xs mt-0.5">{d.customer_email}</p>}
              {d.customer_phone && <p className="text-gray-400 text-xs">{d.customer_phone}</p>}
            </div>
            <div className="bg-surface-elevated rounded-xl p-3 col-span-2 sm:col-span-1">
              <div className="flex items-center gap-2 text-gray-400 text-xs mb-2"><Hash size={13}/> Order Info</div>
              <p className="text-white font-semibold">{fmtTime(d.created_at)}</p>
              <p className="text-gray-400 text-xs mt-0.5">{items.length} items · <span className="text-gold font-semibold">{fmtR(d.total_amount)}</span></p>
            </div>
            {d.delivery_address && (
              <div className="bg-surface-elevated rounded-xl p-3 col-span-2">
                <div className="flex items-center gap-2 text-gray-400 text-xs mb-2"><MapPin size={13}/> Delivery Address</div>
                <p className="text-white text-sm whitespace-pre-line">{d.delivery_address}</p>
              </div>
            )}
            {d.delivery_notes && (
              <div className="bg-surface-elevated rounded-xl p-3 col-span-2">
                <div className="flex items-center gap-2 text-gray-400 text-xs mb-2"><StickyNote size={13}/> Notes</div>
                <p className="text-gray-300 text-sm">{d.delivery_notes}</p>
              </div>
            )}
          </div>

          {/* Items table */}
          <div>
            <p className="text-sm font-semibold text-white mb-2">Order Items ({items.length})</p>
            <div className="bg-surface-elevated rounded-xl overflow-hidden border border-surface-border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-surface-border">
                      <th className="text-left px-3 py-2 text-gray-500 text-xs font-medium">Product</th>
                      <th className="text-left px-3 py-2 text-gray-500 text-xs font-medium">Stock Code</th>
                      <th className="text-center px-3 py-2 text-gray-500 text-xs font-medium">Qty</th>
                      <th className="text-right px-3 py-2 text-gray-500 text-xs font-medium">Unit</th>
                      <th className="text-right px-3 py-2 text-gray-500 text-xs font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, i) => (
                      <tr key={item.id || i} className="border-b border-surface-border/40 last:border-0">
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2.5">
                            {item.primary_image ? (
                              <div className="w-8 h-8 rounded bg-surface-card flex-shrink-0 overflow-hidden flex items-center justify-center">
                                <img
                                  src={`/MappedData/${item.primary_image}`}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  onError={e => {
                                    e.target.style.display='none'
                                    e.target.parentElement.innerHTML='<span style="font-size:18px;line-height:1">🍾</span>'
                                  }}
                                />
                              </div>
                            ) : (
                              <div className="w-8 h-8 rounded bg-surface-card flex items-center justify-center flex-shrink-0">
                                <span style={{fontSize:'18px',lineHeight:1}}>🍾</span>
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="text-white text-xs font-medium leading-tight line-clamp-2">{item.description || '—'}</p>
                              {item.brand && <p className="text-primary text-xs">{item.brand}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 font-mono text-xs text-gray-400">{item.stock_code || '—'}</td>
                        <td className="px-3 py-2.5 text-center text-white font-semibold">{item.quantity}</td>
                        <td className="px-3 py-2.5 text-right text-gray-300 text-xs">{fmtR(item.unit_price)}</td>
                        <td className="px-3 py-2.5 text-right text-white font-semibold text-xs">{fmtR(item.total_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-surface-card border-t border-surface-border">
                      <td colSpan={4} className="px-3 py-2.5 text-right text-gray-400 text-sm font-medium">Order Total</td>
                      <td className="px-3 py-2.5 text-right text-gold font-bold">{fmtR(d.total_amount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-1 border-t border-surface-border flex-wrap gap-3">
            <div className="flex gap-2">
              {transitions.map(action => (
                <button
                  key={action.next}
                  onClick={() => onStatusUpdate(d.id, action.next)}
                  disabled={isUpdating}
                  className={`${action.cls} flex items-center gap-1.5 text-sm`}
                >
                  <action.icon size={14}/>{action.label}
                </button>
              ))}
              {transitions.length === 0 && (
                <span className="text-gray-500 text-sm flex items-center gap-1.5">
                  <CheckCircle size={14} className="text-green-500"/>
                  No further actions available
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={handleCSV} className="btn-secondary flex items-center gap-1.5 text-xs">
                <Download size={13}/> Export CSV
              </button>
              <button
                onClick={() => downloadOrderPosCsv(d.id)}
                className="btn-primary flex items-center gap-1.5 text-xs"
                title="Download the POS import file (stock code + quantity) for this order"
              >
                <Download size={13}/> POS CSV
              </button>
              <button onClick={handlePrint} className="btn-secondary flex items-center gap-1.5 text-xs">
                <Printer size={13}/> Print
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ─── Bulk Status Bar ─────────────────────────────────────────────────────────

function BulkBar({ selectedIds, onClear, onBulkUpdate, isUpdating }) {
  const [bulkStatus, setBulkStatus] = useState('')
  const [showMenu, setShowMenu] = useState(false)

  const apply = () => {
    if (!bulkStatus) return toast.error('Select a status first')
    onBulkUpdate(bulkStatus)
    setShowMenu(false)
    setBulkStatus('')
  }

  return (
    <div className="bg-primary/10 border border-primary/30 rounded-xl p-3 flex items-center justify-between flex-wrap gap-3">
      <div className="flex items-center gap-2 text-primary text-sm font-medium">
        <CheckSquare size={15}/>
        {selectedIds.size} order{selectedIds.size !== 1 ? 's' : ''} selected
      </div>
      <div className="flex items-center gap-2">
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="btn-primary flex items-center gap-1.5 text-xs py-1.5"
          >
            <List size={13}/> Set Status <ChevronDown size={12}/>
          </button>
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 bg-surface-card border border-surface-border rounded-xl shadow-xl z-50 w-44 overflow-hidden">
              {BULK_STATUS_OPTIONS.map(s => {
                const meta = STATUS_META[s]
                const Icon = meta.icon
                return (
                  <button
                    key={s}
                    onClick={() => { setBulkStatus(s); setShowMenu(false) }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-surface-elevated transition-colors text-left"
                  >
                    <Icon size={13} className={meta.color}/> {meta.label}
                  </button>
                )
              })}
            </div>
          )}
        </div>
        {bulkStatus && (
          <button onClick={apply} disabled={isUpdating} className="btn-success text-xs py-1.5">
            Apply &quot;{STATUS_META[bulkStatus]?.label}&quot;
          </button>
        )}
        <button onClick={onClear} className="btn-secondary text-xs py-1.5">Clear</button>
      </div>
    </div>
  )
}

// ─── Main Orders Page ─────────────────────────────────────────────────────────

export default function Orders() {
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [showDateFilter, setShowDateFilter] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(25)
  const qc = useQueryClient()

  // Reset to page 1 whenever a filter or page size changes
  useEffect(() => { setPage(1) }, [statusFilter, search, fromDate, toDate, perPage])

  const { data: ordersData, isLoading } = useQuery({
    queryKey: ['admin', 'orders', page, perPage, statusFilter, search, fromDate, toDate],
    queryFn: () => orderService.getAll({
      skip: (page - 1) * perPage,
      limit: perPage,
      status: statusFilter,
      search: search || undefined,
      from_date: fromDate || undefined,
      to_date: toDate || undefined,
    }),
    keepPreviousData: true,
    staleTime: 30 * 1000,
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, status }) => orderService.updateStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries(['admin', 'orders'])
      qc.invalidateQueries(['admin', 'dashboard'])
      toast.success('Order updated')
    },
    onError: () => toast.error('Failed to update order'),
  })

  const bulkMutation = useMutation({
    mutationFn: ({ ids, status }) => orderService.bulkUpdateStatus([...ids], status),
    onSuccess: (_, vars) => {
      qc.invalidateQueries(['admin', 'orders'])
      toast.success(`${vars.ids.size} orders updated to ${vars.status}`)
      setSelectedIds(new Set())
    },
    onError: () => toast.error('Bulk update failed'),
  })

  const rawOrders = Array.isArray(ordersData) ? ordersData : (ordersData?.items ?? ordersData?.orders ?? [])
  const total = ordersData?.total ?? rawOrders.length
  // server already returns just this page, filtered
  const paginated = rawOrders

  const counts = useMemo(() => ({
    all: ordersData?.total_all ?? rawOrders.length,
    ...(ordersData?.status_counts || {}),
  }), [ordersData, rawOrders.length])

  const totalRevenue = ordersData?.total_revenue ?? rawOrders.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0)

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }, [])

  const toggleAll = () => {
    setSelectedIds(selectedIds.size === paginated.length && paginated.length > 0
      ? new Set()
      : new Set(paginated.map(o => o.id)))
  }

  const handleStatusUpdate = (orderId, newStatus) => {
    updateMutation.mutate({ id: orderId, status: newStatus })
    if (selectedOrder?.id === orderId) setSelectedOrder(prev => prev ? { ...prev, status: newStatus } : null)
  }

  const exportCSV = () => {
    const header = 'Order ID,Customer,Email,Date,Items,Total,Status\n'
    const rows = paginated.map(o =>
      `${o.id},${(o.customer_name||'').replace(/,/g,'')},${o.customer_email||''},${fmt(o.created_at)},${o.item_count||o.items?.length||0},${o.total_amount||0},${o.status}`
    ).join('\n')
    const blob = new Blob([header+rows], {type:'text/csv'})
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob)
    a.download = `orders-${new Date().toISOString().slice(0,10)}.csv`; a.click()
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Orders</h1>
          <p className="text-gray-400 text-sm mt-0.5">
            {total.toLocaleString()} orders · {fmtR(totalRevenue)} total revenue
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCSV} className="btn-secondary flex items-center gap-1.5 text-xs">
            <Download size={13}/> Export CSV
          </button>
          <button onClick={() => qc.invalidateQueries(['admin','orders'])} className="btn-secondary flex items-center gap-2 text-sm">
            <RefreshCw size={14}/> Refresh
          </button>
        </div>
      </div>

      {/* Status filter pills */}
      <div className="flex gap-2 flex-wrap">
        {STATUS_OPTIONS.map(s => {
          const meta = STATUS_META[s]
          const count = counts[s] || 0
          const Icon = s === 'all' ? Package : meta?.icon
          return (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); setSelectedIds(new Set()) }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                statusFilter === s
                  ? 'bg-primary text-white'
                  : 'text-gray-400 bg-surface-card border border-surface-border hover:border-gray-500'
              }`}
            >
              {Icon && <Icon size={12}/>}
              {s === 'all' ? 'All Orders' : meta?.label || s}
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${statusFilter===s?'bg-white/20 text-white':'bg-surface-elevated text-gray-500'}`}>
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Search + date filter */}
      <div className="bg-surface-card border border-surface-border rounded-xl p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            placeholder="Search order ID, customer name or email..."
            className="input w-full pl-9 text-sm py-2"
          />
        </div>
        <button
          onClick={() => setShowDateFilter(!showDateFilter)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
            fromDate || toDate
              ? 'bg-primary/10 border-primary/40 text-primary'
              : 'bg-surface-elevated border-surface-border text-gray-400 hover:text-gray-300'
          }`}
        >
          <Calendar size={13}/> Date Range
          {(fromDate || toDate) && <span className="w-1.5 h-1.5 bg-primary rounded-full"/>}
        </button>
      </div>

      {/* Date range panel */}
      {showDateFilter && (
        <div className="bg-surface-card border border-primary/30 rounded-xl p-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs text-gray-400 block mb-1">From</label>
            <input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} className="input text-sm py-1.5"/>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">To</label>
            <input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} className="input text-sm py-1.5"/>
          </div>
          <button onClick={() => { setFromDate(''); setToDate(''); setShowDateFilter(false) }} className="btn-secondary text-xs">Clear</button>
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <BulkBar
          selectedIds={selectedIds}
          onClear={() => setSelectedIds(new Set())}
          onBulkUpdate={(status) => bulkMutation.mutate({ ids: selectedIds, status })}
          isUpdating={bulkMutation.isLoading}
        />
      )}

      <Pagination page={page} total={total} perPage={perPage} onPerPageChange={setPerPage}
        onChange={p => { setPage(p); setSelectedIds(new Set()) }} position="top"/>

      {/* Orders table */}
      <div className="bg-surface-card border border-surface-border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner size="lg"/></div>
        ) : paginated.length === 0 ? (
          <EmptyState
            icon={Package}
            title="No orders found"
            message={search || statusFilter !== 'all' || fromDate || toDate
              ? 'Try adjusting your filters'
              : 'No orders have been placed yet'}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-border bg-surface-elevated">
                  <th className="px-4 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === paginated.length && paginated.length > 0}
                      onChange={toggleAll}
                      className="rounded border-gray-600 accent-primary"
                    />
                  </th>
                  {['Order', 'Customer', 'Date', 'Items', 'Total', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map(order => {
                  const meta = STATUS_META[order.status] || {}
                  const StatusIcon = meta.icon || Package
                  const isSelected = selectedIds.has(order.id)
                  return (
                    <tr
                      key={order.id}
                      onClick={() => setSelectedOrder(order)}
                      className={`border-b border-surface-border/50 transition-colors cursor-pointer ${
                        (isSelected || selectedOrder?.id === order.id) ? 'bg-primary/10' : 'hover:bg-surface-elevated/30'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(order.id)}
                          className="rounded border-gray-600 accent-primary"
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-primary font-semibold text-sm">#{order.id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-white text-sm font-medium">{order.customer_name || `ID: ${order.customer_id}`}</p>
                          {order.customer_email && <p className="text-gray-500 text-xs">{order.customer_email}</p>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmt(order.order_date || order.created_at)}</td>
                      <td className="px-4 py-3 text-gray-300 text-center">{order.item_count ?? order.items?.length ?? '—'}</td>
                      <td className="px-4 py-3 font-semibold text-white whitespace-nowrap">{fmtR(order.total_amount)}</td>
                      <td className="px-4 py-3">
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${meta.bg || ''} ${meta.color || 'text-gray-400'} border ${meta.border || 'border-gray-700'}`}>
                          <StatusIcon size={11}/>
                          {meta.label || order.status}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button
                            onClick={e => { e.stopPropagation(); setSelectedOrder(order) }}
                            title="View order detail"
                            className="px-2 py-1 rounded text-xs font-medium flex items-center gap-1 bg-surface-elevated text-gray-300 border border-surface-border hover:text-white hover:border-primary/50 transition-colors"
                          >
                            <Eye size={11}/> View
                          </button>
                          <button
                            onClick={e => { e.stopPropagation(); downloadOrderPosCsv(order.id) }}
                            title="Download POS order file (stock code + quantity)"
                            className="px-2 py-1 rounded text-xs font-medium flex items-center gap-1 bg-blue-500/15 text-blue-300 border border-blue-500/30 hover:bg-blue-500/25 transition-colors"
                          >
                            <Download size={11}/> POS CSV
                          </button>
                          {(STATUS_TRANSITIONS[order.status] || []).map(action => (
                            <button
                              key={action.next}
                              onClick={e => { e.stopPropagation(); handleStatusUpdate(order.id, action.next) }}
                              disabled={updateMutation.isLoading}
                              title={action.label}
                              className={`px-2 py-1 rounded text-xs font-medium ${action.cls} flex items-center gap-1`}
                            >
                              <action.icon size={11}/>{action.label}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Pagination page={page} total={total} perPage={perPage} onPerPageChange={setPerPage}
        onChange={p => { setPage(p); setSelectedIds(new Set()) }} position="bottom"/>

      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onStatusUpdate={handleStatusUpdate}
          isUpdating={updateMutation.isLoading}
        />
      )}
    </div>
  )
}
